/**
 * ③ 履歴詳細 (マスタ変更履歴 SBI 7)。
 *
 * 1 つの版の「いつ・誰が・何を」と、項目ごとの変更前 / 変更後を表示する。
 * 変更通知メールのリンク (`entity_key` + `rev`) の着地点でもある。
 *
 * - 差分は API が返す `changes` (除外ルールと日本語ラベル適用済み) をそのまま表示する。
 *   完全なスナップショット (`before` / `after`) は内部の項目名を含むため画面には出さない。
 * - 作成の版は変更前が「(なし)」、削除の版は変更後が「(なし)」になる (API の差分のとおり)。
 * - 存在しない版は 404。エラーではなく「履歴なし」として案内する。
 * - 「最新との比較」は押したときに初めて `diff` を呼ぶ。
 * - 復旧 (削除したデータを戻す操作) は ① の各行にある。この画面には置かない。
 * - 取得条件が変わるときは呼び出し側が `key` で作り直す (`useMasterHistoryLoad` を参照)。
 *
 * 権限: 画面の出し分けは利便性のため。対象のマスタ種別の機能を持たない場合は API を呼ばずに
 * 説明を出す (バックエンドは 403)。
 */
import { useMemo, useState } from 'react';
import type React from 'react';

import { ApiError } from '../api/common';
import { MasterHistoryClient } from '../api/masterHistory';
import type { MasterHistoryDetail } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import type { AppConfig } from '../config';
import { useMasterHistoryLoad } from '../hooks/useMasterHistoryLoad';
import { buildEntityPath, masterTypeOfEntityKey } from '../pages/masterHistoryView';
import { describeApiErrorMessage } from '../utils/apiErrorMessage';
import { formatDateTime } from '../utils/dateTime';
import {
  MASTER_HISTORY_ACTION_LABEL,
  MASTER_HISTORY_TYPE_LABEL,
} from '../utils/masterHistoryLabels';

import { MasterHistoryChangeTable } from './MasterHistoryChangeTable';
import { MasterHistoryDiffPanel } from './MasterHistoryDiffPanel';
import { MasterHistorySourceBackButton } from './MasterHistorySourceBackButton';
import { Spinner } from './Spinner';
import { StatusBanner } from './StatusBanner';

export const DETAIL_FORBIDDEN_MESSAGE =
  'このデータの変更履歴を表示する権限がありません。管理者に確認してください';
export const DETAIL_NOT_FOUND_MESSAGE = '指定された版の履歴は見つかりませんでした';
export const DETAIL_NO_CHANGES_MESSAGE = '表示できる変更内容はありません';

const NO_NAME_LABEL = '(名称なし)';
const NONE_LABEL = '—';

export interface MasterHistoryDetailScreenProps {
  config: AppConfig;
  entityKey: string;
  /** 履歴の版 (ULID) */
  version: string;
  /** 対象のマスタ種別の機能を持たないとき true (API を呼ばない) */
  forbidden: boolean;
  onNavigate: (path: string) => void;
  /** ヘッダーのタブから入ったとき true (「閉じる」「遷移元へ戻る」を出さない) */
  hideSourceBack?: boolean;
}

export function MasterHistoryDetailScreen(
  props: MasterHistoryDetailScreenProps,
): React.JSX.Element {
  const { config, entityKey, version, forbidden, onNavigate, hideSourceBack = false } = props;
  const { getIdToken } = useAuth();
  const client = useMemo(
    () => new MasterHistoryClient({ getIdToken, config }),
    [getIdToken, config],
  );
  const load = useMemo(
    () => (): Promise<MasterHistoryDetail | null> =>
      forbidden ? Promise.resolve(null) : client.get(version, entityKey),
    [client, entityKey, version, forbidden],
  );
  const { data, loading, error, reload } = useMasterHistoryLoad<MasterHistoryDetail | null>(load);
  const [diffOpen, setDiffOpen] = useState(false);
  const notFound = error instanceof ApiError && error.status === 404;

  function renderBody(): React.JSX.Element {
    if (forbidden) {
      return (
        <p role="alert" data-testid="master-history-detail-forbidden">
          {DETAIL_FORBIDDEN_MESSAGE}
        </p>
      );
    }
    if (loading) {
      return (
        <p data-testid="master-history-detail-loading">
          <Spinner /> 読み込み中...
        </p>
      );
    }
    if (notFound) {
      return (
        <p role="status" data-testid="master-history-detail-notfound">
          {DETAIL_NOT_FOUND_MESSAGE}
        </p>
      );
    }
    if (error !== null) {
      return (
        <StatusBanner variant="error" data-testid="master-history-detail-error">
          <span>{describeApiErrorMessage(error)}</span>{' '}
          <button type="button" onClick={reload} data-testid="master-history-detail-retry">
            再読み込み
          </button>
        </StatusBanner>
      );
    }
    if (data === null) return <></>;
    return renderDetail(data);
  }

  function renderDetail(detail: MasterHistoryDetail): React.JSX.Element {
    const changes = detail.changes ?? [];
    return (
      <>
        <dl data-testid="master-history-detail-summary">
          <dt>変更日時</dt>
          <dd>{formatDateTime(detail.changed_at)}</dd>
          <dt>データ種別</dt>
          <dd>{MASTER_HISTORY_TYPE_LABEL[detail.master_type] ?? detail.master_type}</dd>
          <dt>対象名</dt>
          <dd>{detail.display_name || NO_NAME_LABEL}</dd>
          <dt>操作</dt>
          <dd>{MASTER_HISTORY_ACTION_LABEL[detail.action] ?? detail.action}</dd>
          <dt>変更者</dt>
          <dd>{detail.actor || NONE_LABEL}</dd>
          <dt>変更概要</dt>
          <dd>{detail.summary || NONE_LABEL}</dd>
        </dl>

        <h3>変更内容</h3>
        {changes.length === 0 ? (
          <p data-testid="master-history-detail-no-changes">{DETAIL_NO_CHANGES_MESSAGE}</p>
        ) : (
          <MasterHistoryChangeTable
            changes={changes}
            beforeHeading="変更前"
            afterHeading="変更後"
            testIdPrefix="master-history-detail-changes"
          />
        )}

        <h3>最新との比較</h3>
        <div className="sort-filter-bar">
          <button
            type="button"
            onClick={() => setDiffOpen((open) => !open)}
            aria-expanded={diffOpen}
            data-testid="master-history-detail-diff-toggle"
          >
            {diffOpen ? '比較を閉じる' : 'この版と最新を比較する'}
          </button>
        </div>
        {diffOpen && (
          <MasterHistoryDiffPanel client={client} entityKey={entityKey} version={version} />
        )}
      </>
    );
  }

  return (
    <section
      className="section section--page list-page"
      data-testid="master-history-detail"
      data-entity-key={entityKey}
      data-rev={version}
    >
      <h2>変更履歴の詳細</h2>
      <div className="sort-filter-bar">
        <button
          type="button"
          onClick={() => onNavigate(buildEntityPath(entityKey))}
          data-testid="master-history-detail-back"
        >
          対象データの履歴一覧へ戻る
        </button>
        <MasterHistorySourceBackButton
          masterType={masterTypeOfEntityKey(entityKey)}
          forbidden={forbidden}
          hidden={hideSourceBack}
          onNavigate={onNavigate}
          testId="master-history-detail-back-source"
        />
      </div>
      <div className="list-page__body">{renderBody()}</div>
    </section>
  );
}
